import type { ReactElement } from 'react';
import { useUIDSeed } from 'react-uid';
import { useEffect, useState, useCallback } from 'react';
import useAsyncEffect from 'hooks/useAsyncEffect';

import { Route, ApiRoute } from 'config';
import { OrderHistoryProvider } from 'contexts/OrderHistory';
import { monthStart, monthEnd, addMonths } from 'utils/dateTimes';
import { getQueryParam } from 'utils/location';
import { get } from 'utils/network';

import type { IOrderHistory } from 'models/OrderHistory';
import OrderHistoryModel from 'models/OrderHistory';

import { Component as CurrentMenuIcon } from 'icons/icon-menu--current.svg';
import MenuSkeleton from 'components/shared/skeletons/MenuSkeleton';
import OrderHistoryMenu from './OrderHistoryMenu';
import Header from './Header';

function OrderHistory(): ReactElement {
  const [ordersHistory, setOrdersHistory] = useState<OrderHistoryModel>(
    {
      previous_month_orders_exists: false,
      next_month_orders_exists: false,
      orders: [],
      // @ts-expect-error TODO: fix null safety
      period: null,
    },
  );

  const getCurrentPeriod = useCallback(() => {
    // @ts-expect-error TODO: fix null safety
    const monthParam = parseInt(getQueryParam('month'), 10);

    return monthParam
      ? new Date(Math.trunc(monthParam / 100), (monthParam % 100) - 1)
      : null;
  }, []);

  const seed = useUIDSeed();
  const [loading, setLoading] = useState(true);
  const [currentPeriod, setCurrentPeriod] = useState(getCurrentPeriod());
  const hasNextOrders = ordersHistory.next_month_orders_exists;
  const hasPreviousOrders = ordersHistory.previous_month_orders_exists;

  const navigateTo = (direction: 'next' | 'prev', period: Date) => {
    setCurrentPeriod(() => {
      const newDate = direction === 'next'
        ? addMonths(period, 1)
        : addMonths(period, -1);

      const year = newDate.getFullYear();
      const month = (newDate.getMonth() + 1).toString().padStart(2, '0');

      window.history.pushState(
        null,
        '',
        `${Route.ORDER_HISTORY}/?month=${year}${month}`,
      );

      return newDate;
    });
  };

  useAsyncEffect(async (isMounted) => {
    setLoading(true);

    const queryParams = currentPeriod
      ? [`delivery_from=${monthStart(currentPeriod)}`, `delivery_to=${monthEnd(currentPeriod)}`]
      : [];

    const { parsedBody, ok } = await get<IOrderHistory>(
      `${ApiRoute.ORDER_HISTORY}/?${queryParams.join('&')}`,
    );

    if (isMounted.current && ok) {
      setOrdersHistory(new OrderHistoryModel(parsedBody));
      setLoading(false);
    }
  }, [currentPeriod]);

  useEffect(() => {
    const listener = () => setCurrentPeriod(getCurrentPeriod());

    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, [getCurrentPeriod]);

  return (
    <div className="col-md-10 col-lg-10">
      <Header
        hasPreviousOrders={hasPreviousOrders}
        hasNextOrders={hasNextOrders}
        currentPeriod={ordersHistory.period}
        navigateTo={navigateTo}
      />

      {loading && [...Array(3)].map((el, i) => (
        <MenuSkeleton key={seed(i)} />
      ))}

      {!loading && (ordersHistory.orders.length ? (
        ordersHistory.orders.map((item) => (
          <OrderHistoryProvider value={item} key={item.menu_id}>
            <OrderHistoryMenu />
          </OrderHistoryProvider>
        ))
      ) : (
        <div className="c-card">
          <div className="c-card__content">
            <div className="c-orders-no-results">
              <CurrentMenuIcon width={50} />
              <p>
                <br />
                You did not receive a box this month.
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default OrderHistory;
